import { createContext, useEffect, useMemo, useState } from "react";

import {
  acquireAuthSession,
  releaseAuthSession,
} from "../../services/auth/authSessionRuntime";
import { EXTERNAL_STATUS } from "../../services/auth/authStateMachine";

export const AUTH_SESSION_STATUS = {
  LOADING: EXTERNAL_STATUS.LOADING,
  AUTHENTICATED: EXTERNAL_STATUS.AUTHENTICATED,
  UNAUTHENTICATED: EXTERNAL_STATUS.UNAUTHENTICATED,
};

export const AuthSessionContext = createContext({
  status: AUTH_SESSION_STATUS.LOADING,
  authMachine: null,
});

// Exposes the shell's sign-in session (services/auth/authSessionRuntime) to
// components. Mounted by the app shell: the session ends when it unmounts.
//
// Context exposes:
//   status      — external status (loading | authenticated | unauthenticated)
//   authMachine — the machine instance, so LogoutService/LoginService can
//                 send events (LOGOUT_REQUESTED) or read internal state
//                 (SESSION_EXPIRED).
export const AuthSessionProvider = ({ children }) => {
  const [session] = useState(acquireAuthSession);

  useEffect(() => () => releaseAuthSession(session), [session]);

  // React state rather than useSyncExternalStore on purpose: a renewal
  // re-evaluates the session (authenticated -> loading -> authenticated) across
  // a few microtasks, and batched state updates never render that brief
  // "loading". Rendering it would make PrivateRoute unmount the page.
  const [status, setStatus] = useState(session.getStatus);

  useEffect(() => {
    const unsubscribe = session.subscribe((next) => setStatus(next));
    // The session may have moved on between this render and the subscription.
    setStatus(session.getStatus());
    return unsubscribe;
  }, [session]);

  const machine = session.machine;
  const value = useMemo(
    () => ({
      status,
      authMachine: machine,
    }),
    [status, machine],
  );

  return (
    <AuthSessionContext.Provider value={value}>
      {children}
    </AuthSessionContext.Provider>
  );
};
